import { useCallback, useEffect, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { getSafeRandomId } from "@/lib/random-id";
import { isMissingSupabaseEntityError } from "@/lib/supabase-errors";

export type NormalizedRect = {
  x: number;
  y: number;
  w: number;
  h: number;
};

export type ReaderAnnotationKind = "highlight" | "ink" | "shape" | "note";

export type ReaderAnnotation = {
  id: string;
  resource_id: string;
  page_number: number;
  kind: ReaderAnnotationKind;
  color: string | null;
  rects: NormalizedRect[];
  points: unknown[] | null;
  shape_data: Record<string, unknown> | null;
  note_text: string | null;
  selected_text: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  pendingSync?: boolean;
};

type PendingOperation =
  | { type: "upsert"; annotation: ReaderAnnotation }
  | { type: "delete"; annotationId: string };

const STORAGE_PREFIX = "athr.reader-annotations";
const QUEUE_PREFIX = "athr.reader-annotations.queue";
const DEVICE_ID_PREFIX = "athr.reader-annotations.device";
const STORAGE_UPDATED_EVENT = "athr-reader-annotations-updated";

const clampUnit = (value: number) => Math.max(0, Math.min(1, value));

const sanitizeRects = (value: unknown): NormalizedRect[] => {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => {
      if (!item || typeof item !== "object") return null;
      const rect = item as Partial<NormalizedRect>;
      if (![rect.x, rect.y, rect.w, rect.h].every((part) => typeof part === "number")) {
        return null;
      }
      return {
        x: clampUnit(rect.x!),
        y: clampUnit(rect.y!),
        w: clampUnit(rect.w!),
        h: clampUnit(rect.h!),
      };
    })
    .filter((item): item is NormalizedRect => Boolean(item));
};

const isPlainRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const normalizeAnnotation = (value: unknown): ReaderAnnotation | null => {
  if (!isPlainRecord(value)) return null;
  const id = typeof value.id === "string" ? value.id : null;
  const resourceId = typeof value.resource_id === "string" ? value.resource_id : null;
  const pageNumber = Number(value.page_number);
  const kind = value.kind;
  if (!id || !resourceId || !Number.isFinite(pageNumber)) return null;
  if (kind !== "highlight" && kind !== "ink" && kind !== "shape" && kind !== "note") return null;

  return {
    id,
    resource_id: resourceId,
    page_number: Math.max(1, Math.floor(pageNumber)),
    kind,
    color: typeof value.color === "string" ? value.color : null,
    rects: sanitizeRects(value.rects),
    points: Array.isArray(value.points) ? value.points : null,
    shape_data: isPlainRecord(value.shape_data) ? value.shape_data : null,
    note_text: typeof value.note_text === "string" ? value.note_text : null,
    selected_text: typeof value.selected_text === "string" ? value.selected_text : null,
    metadata: isPlainRecord(value.metadata) ? value.metadata : {},
    created_at: typeof value.created_at === "string" ? value.created_at : new Date().toISOString(),
    updated_at: typeof value.updated_at === "string" ? value.updated_at : new Date().toISOString(),
    pendingSync: value.pendingSync === true,
  };
};

const readJson = <T,>(key: string, fallback: T): T => {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
};

const writeJson = (key: string, value: unknown) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Ignore storage quota failures and keep remote sync available.
  }
};

const getStorageKey = (userId: string, resourceId: string) => `${STORAGE_PREFIX}:${userId}:${resourceId}`;
const getQueueKey = (userId: string, resourceId: string) => `${QUEUE_PREFIX}:${userId}:${resourceId}`;
const getDeviceKey = (userId: string) => `${DEVICE_ID_PREFIX}:${userId}`;

const getDeviceId = (userId: string) => {
  const key = getDeviceKey(userId);
  const existing = readJson<string | null>(key, null);
  if (existing) return existing;
  const next = getSafeRandomId();
  writeJson(key, next);
  return next;
};

const readCachedAnnotations = (userId: string, resourceId: string) =>
  readJson<ReaderAnnotation[]>(getStorageKey(userId, resourceId), [])
    .map(normalizeAnnotation)
    .filter((item): item is ReaderAnnotation => Boolean(item));

const writeCachedAnnotations = (userId: string, resourceId: string, annotations: ReaderAnnotation[]) => {
  writeJson(getStorageKey(userId, resourceId), annotations);
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(STORAGE_UPDATED_EVENT));
  }
};

const readPendingQueue = (userId: string, resourceId: string) =>
  readJson<PendingOperation[]>(getQueueKey(userId, resourceId), []).filter((item) => {
    if (!item || typeof item !== "object" || !("type" in item)) return false;
    if (item.type === "delete") return typeof item.annotationId === "string";
    return item.type === "upsert" && Boolean(normalizeAnnotation(item.annotation));
  });

const writePendingQueue = (userId: string, resourceId: string, queue: PendingOperation[]) => {
  writeJson(getQueueKey(userId, resourceId), queue);
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(STORAGE_UPDATED_EVENT));
  }
};

export const getPendingReaderAnnotationSyncCount = (userId?: string | null) => {
  if (typeof window === "undefined" || !userId) return 0;

  let total = 0;
  const queueKeyPrefix = `${QUEUE_PREFIX}:${userId}:`;
  for (let index = 0; index < window.localStorage.length; index += 1) {
    const key = window.localStorage.key(index);
    if (!key || !key.startsWith(queueKeyPrefix)) continue;
    total += readJson<PendingOperation[]>(key, []).length;
  }
  return total;
};

export const getReaderAnnotationSyncEventName = () => STORAGE_UPDATED_EVENT;

const getOperationKey = (operation: PendingOperation) =>
  operation.type === "delete" ? operation.annotationId : operation.annotation.id;

const collapsePendingQueue = (queue: PendingOperation[]) => {
  const collapsed = new Map<string, PendingOperation>();
  for (const item of queue) {
    const normalized =
      item.type === "upsert"
        ? normalizeAnnotation(item.annotation)
          ? { type: "upsert" as const, annotation: normalizeAnnotation(item.annotation)! }
          : null
        : item;
    if (!normalized) continue;
    const key = getOperationKey(normalized);
    if (collapsed.has(key)) {
      collapsed.delete(key);
    }
    collapsed.set(key, normalized);
  }
  return Array.from(collapsed.values());
};

const getPendingDeleteIds = (queue: PendingOperation[]) =>
  new Set(queue.filter((item): item is Extract<PendingOperation, { type: "delete" }> => item.type === "delete").map((item) => item.annotationId));

const mergeAnnotations = (localItems: ReaderAnnotation[], remoteItems: ReaderAnnotation[]) => {
  const next = new Map<string, ReaderAnnotation>();
  [...remoteItems, ...localItems].forEach((annotation) => {
    const previous = next.get(annotation.id);
    if (!previous || previous.updated_at <= annotation.updated_at) {
      next.set(annotation.id, annotation);
    }
  });
  return Array.from(next.values()).sort((a, b) => {
    if (a.page_number !== b.page_number) return a.page_number - b.page_number;
    return a.created_at.localeCompare(b.created_at);
  });
};

const toSupabasePayload = (userId: string, annotation: ReaderAnnotation) => ({
  id: annotation.id,
  user_id: userId,
  resource_id: annotation.resource_id,
  page_number: annotation.page_number,
  kind: annotation.kind,
  color: annotation.color,
  rects: annotation.rects,
  points: annotation.points,
  shape_data: annotation.shape_data,
  note_text: annotation.note_text,
  selected_text: annotation.selected_text,
  metadata: annotation.metadata,
  created_at: annotation.created_at,
  updated_at: annotation.updated_at,
});

async function flushQueue(userId: string, resourceId: string) {
  const queue = collapsePendingQueue(readPendingQueue(userId, resourceId));
  if (!queue.length) return;

  const remaining: PendingOperation[] = [];
  const deleteItems: Extract<PendingOperation, { type: "delete" }>[] = [];
  const upsertItems: Extract<PendingOperation, { type: "upsert" }>[] = [];

  for (const item of queue) {
    if (item.type === "delete") {
      deleteItems.push(item);
    } else if (item.type === "upsert") {
      upsertItems.push(item);
    }
  }

  if (deleteItems.length > 0) {
    const deleteIds = deleteItems.map((item) => item.annotationId);
    const { error } = await supabase
      .from("reader_annotations" as never)
      .delete()
      .in("id", deleteIds)
      .eq("user_id", userId);

    if (error && !isMissingSupabaseEntityError(error)) {
      remaining.push(...deleteItems);
    }
  }

  const validUpsertItems = upsertItems.filter((item) => normalizeAnnotation(item.annotation));
  if (validUpsertItems.length > 0) {
    const upsertPayloads = validUpsertItems.map((item) =>
      toSupabasePayload(userId, normalizeAnnotation(item.annotation)!)
    );

    const { error } = await supabase
      .from("reader_annotations" as never)
      .upsert(upsertPayloads as never, { onConflict: "id" });

    if (error && !isMissingSupabaseEntityError(error)) {
      remaining.push(...validUpsertItems);
    }
  }

  writePendingQueue(userId, resourceId, collapsePendingQueue(remaining));
}

export function useReaderAnnotations(resourceId?: string) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => ["reader-annotations", resourceId, user?.id], [resourceId, user?.id]);

  const query = useQuery({
    queryKey,
    enabled: Boolean(resourceId && user?.id),
    queryFn: async () => {
      const userId = user!.id;
      const pendingQueue = collapsePendingQueue(readPendingQueue(userId, resourceId!));
      const pendingDeleteIds = getPendingDeleteIds(pendingQueue);
      const localItems = readCachedAnnotations(userId, resourceId!).filter((item) => !pendingDeleteIds.has(item.id));
      try {
        const { data, error } = await supabase
          .from("reader_annotations" as never)
          .select("id,resource_id,page_number,kind,color,rects,points,shape_data,note_text,selected_text,metadata,created_at,updated_at")
          .eq("resource_id", resourceId!)
          .eq("user_id", userId)
          .order("page_number", { ascending: true })
          .order("created_at", { ascending: true });

        if (error) {
          if (isMissingSupabaseEntityError(error)) {
            return localItems;
          }
          return localItems;
        }

        const remoteItems = ((data as unknown[]) || [])
          .map(normalizeAnnotation)
          .filter((item): item is ReaderAnnotation => Boolean(item) && item !== null && !pendingDeleteIds.has(item.id));
        const merged = mergeAnnotations(localItems, remoteItems);
        writeCachedAnnotations(userId, resourceId!, merged);
        return merged;
      } catch {
        return localItems;
      }
    },
    staleTime: 10_000,
  });

  useEffect(() => {
    if (!resourceId || !user?.id) return;
    void flushQueue(user.id, resourceId);

    const handleOnline = () => {
      void flushQueue(user.id, resourceId).then(() => {
        void queryClient.invalidateQueries({ queryKey });
      });
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        void flushQueue(user.id, resourceId);
      }
    };
    const handlePageHide = () => {
      void flushQueue(user.id, resourceId);
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("pagehide", handlePageHide);
    window.addEventListener("beforeunload", handlePageHide);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("pagehide", handlePageHide);
      window.removeEventListener("beforeunload", handlePageHide);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [queryClient, queryKey, resourceId, user?.id]);

  const upsertLocal = useCallback(
    (annotation: ReaderAnnotation) => {
      if (!resourceId || !user?.id) return;
      queryClient.setQueryData<ReaderAnnotation[]>(queryKey, (current = []) => {
        const merged = mergeAnnotations(
          [
            ...current.filter((item) => item.id !== annotation.id),
            {
              ...annotation,
              pendingSync: true,
            },
          ],
          [],
        );
        writeCachedAnnotations(user.id, resourceId, merged);
        return merged;
      });
    },
    [queryClient, queryKey, resourceId, user?.id],
  );

  const queueMutation = useCallback(
    (operation: PendingOperation) => {
      if (!resourceId || !user?.id) return;
      const nextQueue = collapsePendingQueue([...readPendingQueue(user.id, resourceId), operation]);
      writePendingQueue(user.id, resourceId, nextQueue);
      if (navigator.onLine) {
        void flushQueue(user.id, resourceId).then(() => {
          void queryClient.invalidateQueries({ queryKey });
        });
      }
    },
    [queryClient, queryKey, resourceId, user?.id],
  );

  const createHighlightMutation = useMutation({
    mutationFn: async ({
      pageNumber,
      rects,
      color,
      selectedText,
      points,
      metadata,
    }: {
      pageNumber: number;
      rects: NormalizedRect[];
      color: string;
      selectedText: string;
      points?: unknown[] | null;
      metadata?: Record<string, unknown>;
    }) => {
      if (!resourceId || !user?.id) {
        throw new Error("Missing reader context");
      }

      const now = new Date().toISOString();
      const deviceId = getDeviceId(user.id);
      const annotation: ReaderAnnotation = {
        id: getSafeRandomId(),
        resource_id: resourceId,
        page_number: Math.max(1, Math.floor(pageNumber)),
        kind: "highlight",
        color,
        rects: sanitizeRects(rects),
        points: Array.isArray(points) ? points : null,
        shape_data: null,
        note_text: null,
        selected_text: selectedText,
        metadata: {
          ...metadata,
          device_id: deviceId,
          client_updated_at: now,
        },
        created_at: now,
        updated_at: now,
        pendingSync: true,
      };

      upsertLocal(annotation);
      queueMutation({ type: "upsert", annotation });
      return annotation;
    },
  });

  const upsertAnnotationMutation = useMutation({
    mutationFn: async (annotationInput: ReaderAnnotation) => {
      if (!resourceId || !user?.id) {
        throw new Error("Missing reader context");
      }

      const normalized = normalizeAnnotation({
        ...annotationInput,
        resource_id: resourceId,
        updated_at: new Date().toISOString(),
        pendingSync: true,
      });

      if (!normalized) {
        throw new Error("Invalid annotation");
      }

      upsertLocal(normalized);
      queueMutation({ type: "upsert", annotation: normalized });
      return normalized;
    },
  });

  const deleteAnnotationMutation = useMutation({
    mutationFn: async (annotationId: string) => {
      if (!resourceId || !user?.id) {
        throw new Error("Missing reader context");
      }

      queryClient.setQueryData<ReaderAnnotation[]>(queryKey, (current = []) => {
        const next = current.filter((item) => item.id !== annotationId);
        writeCachedAnnotations(user.id, resourceId, next);
        return next;
      });

      queueMutation({ type: "delete", annotationId });
    },
  });

  const annotationsByPage = useMemo(() => {
    const grouped = new Map<number, ReaderAnnotation[]>();
    for (const annotation of query.data ?? []) {
      const pageItems = grouped.get(annotation.page_number) ?? [];
      pageItems.push(annotation);
      grouped.set(annotation.page_number, pageItems);
    }
    return grouped;
  }, [query.data]);

  return {
    annotations: query.data ?? [],
    annotationsByPage,
    isLoading: query.isLoading,
    isSaving: createHighlightMutation.isPending || upsertAnnotationMutation.isPending || deleteAnnotationMutation.isPending,
    createHighlight: createHighlightMutation.mutateAsync,
    upsertAnnotation: upsertAnnotationMutation.mutateAsync,
    deleteAnnotation: deleteAnnotationMutation.mutateAsync,
    reload: query.refetch,
  };
}
